import { accountingPeriodListQuerySchema, type AccountingPeriodListQuery, type AccountingPeriodView, type Page } from '@chukua/common';
import { useEffect, useState } from 'react';
import { useListQuery } from '../../../shared/hooks/use-list-query';
import { toListParams } from '../../../shared/policies/list-params';
import { ApiError } from '../../../shared/shared.api';
import { listAccountingPeriods } from '../accounting-periods.api';

/** `queryKey` records which query the loaded page answers, so a newer query shows as refreshing over the old rows. */
export type AccountingPeriodsState =
  { status: 'loading' } | { status: 'loaded'; page: Page<AccountingPeriodView>; queryKey: string } | { status: 'forbidden' } | { status: 'error' };

const keyOf = (query: AccountingPeriodListQuery) => toListParams(query).toString();

function toErrorState(err: unknown): AccountingPeriodsState {
  return { status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' };
}

/** The page of periods the URL's list query asks for, re-fetched whenever that query changes. */
export function useAccountingPeriodList() {
  const list = useListQuery(accountingPeriodListQuerySchema);
  const { query } = list;
  const [state, setState] = useState<AccountingPeriodsState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    listAccountingPeriods(query)
      .then((page) => {
        if (!cancelled) setState({ status: 'loaded', page, queryKey: keyOf(query) });
      })
      .catch((err) => {
        if (!cancelled) setState(toErrorState(err));
      });
    return () => {
      cancelled = true;
    };
  }, [query]);

  const isRefreshing = state.status === 'loaded' && state.queryKey !== keyOf(query);

  // An unfiltered, loaded, empty list means no period exists at all; a search with no match is not that case.
  const hasNoPeriods = state.status === 'loaded' && state.page.total === 0 && !list.isFiltered;

  async function reload(): Promise<void> {
    try {
      setState({ status: 'loaded', page: await listAccountingPeriods(query), queryKey: keyOf(query) });
    } catch (err) {
      setState(toErrorState(err));
    }
  }

  return { state, list, isRefreshing, hasNoPeriods, reload };
}
